import { NodeViewProps } from '@tiptap/core';
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';

import { codeblockExtension as codeblockExtensionBase } from '@copera/tiptap/extensions';
import { useSnackbar } from 'notistack';
import { CodeHighlight, lowlight } from 'src/components/CodeHighlight';
import useCopyToClipboard from 'src/hooks/useCopyToClipboard';
import { useLocales } from 'src/locales';

const CodeBlockComponent = ({
  extension,
  node,
  updateAttributes,
}: NodeViewProps) => {
  const { copy } = useCopyToClipboard();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  const selectedLang = node.attrs.language as string;

  const handleCopyCode = async () => {
    await copy((node?.content?.content?.[0] as any)?.text);
    enqueueSnackbar(translate('shared.editor.code_in_your_clipboard'));
  };

  return (
    <NodeViewWrapper
      className={`${extension.options.languageClassPrefix}${selectedLang}`}
    >
      <CodeHighlight
        language={selectedLang}
        onLanguageChange={(lang) => {
          updateAttributes({ language: lang });
        }}
        copyCode={handleCopyCode}
      >
        <NodeViewContent />
      </CodeHighlight>
    </NodeViewWrapper>
  );
};

export const codeblockExtension = codeblockExtensionBase
  .extend({
    addNodeView() {
      return ReactNodeViewRenderer(CodeBlockComponent);
    },
    addCommandOption() {
      return [
        {
          icon: 'tabler:code',
          title: 'Block Code',
          description: 'Insert colored code blocks in your document',
          command: ({ editor, range }) => {
            editor
              .chain()
              .deleteRange(range)
              .toggleCodeBlock()
              .focus(range.from)
              .run();
          },
        },
      ];
    },
  })
  .configure({
    lowlight,
  });
